import {
  NAV_BIND_EDGE_ON,
  NAV_BIND_PATH_DIRECTIONS,
  NAV_BIND_SOURCES,
  NAV_PAGE_TEMPLATES,
  NAV_SLOT_REGIONS,
  NAV_SLOT_TYPES,
  NAV_TEXT_TONES,
  type NavBindEdgeOn,
  type NavBindPathDirection,
  type NavBindPathHop,
  type NavBindSource,
  type NavPageRecord,
  type NavPageSlotRecord,
  type NavPageTemplate,
  type NavSlotPart,
  type NavSlotRegion,
  type NavSlotType,
  type NavSlotVariant,
  type NavTextTone,
  SLOT_LANDING_FORMATS,
  type SlotLandingFormat,
} from "./types";

const optionalString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const parseBindPath = (value: unknown): NavBindPathHop[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }

  const hops: NavBindPathHop[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") {
      return undefined;
    }

    const hop = item as { resource?: unknown; version?: unknown; direction?: unknown };
    const resource = optionalString(hop.resource);
    if (!resource) {
      return undefined;
    }

    const directionRaw = optionalString(hop.direction);
    const direction =
      directionRaw && (NAV_BIND_PATH_DIRECTIONS as readonly string[]).includes(directionRaw)
        ? (directionRaw as NavBindPathDirection)
        : undefined;

    hops.push({
      resource,
      version: optionalString(hop.version) ?? "1.0.0",
      ...(direction ? { direction } : {}),
    });
  }

  if (hops.length < 2) {
    return undefined;
  }

  return hops;
};

const stringList = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const keys: string[] = [];
  for (const item of value) {
    if (typeof item === "string" && item.length > 0 && !keys.includes(item)) {
      keys.push(item);
    }
  }

  return keys.length > 0 ? keys : undefined;
};

const isTemplate = (value: unknown): value is NavPageTemplate =>
  typeof value === "string" && (NAV_PAGE_TEMPLATES as readonly string[]).includes(value);

const isRegion = (value: unknown): value is NavSlotRegion =>
  typeof value === "string" && (NAV_SLOT_REGIONS as readonly string[]).includes(value);

const isSlotType = (value: unknown): value is NavSlotType =>
  typeof value === "string" && (NAV_SLOT_TYPES as readonly string[]).includes(value);

const isBindSource = (value: unknown): value is NavBindSource =>
  typeof value === "string" && (NAV_BIND_SOURCES as readonly string[]).includes(value);

const isBindEdgeOn = (value: unknown): value is NavBindEdgeOn =>
  typeof value === "string" && (NAV_BIND_EDGE_ON as readonly string[]).includes(value);

const isTone = (value: unknown): value is NavTextTone =>
  typeof value === "string" && (NAV_TEXT_TONES as readonly string[]).includes(value);

const isLandingFormat = (value: unknown): value is SlotLandingFormat =>
  typeof value === "string" && (SLOT_LANDING_FORMATS as readonly string[]).includes(value);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseBindings = (value: Record<string, unknown>): Record<string, string> => {
  const bindings: Record<string, string> = {};
  for (const [slotKey, viewName] of Object.entries(value)) {
    const view = optionalString(viewName);
    if (slotKey.length > 0 && view) {
      bindings[slotKey] = view;
    }
  }

  return bindings;
};

/** Well-formed variants in authored order: unique keys, a label, a bindings map, one default. */
const parseVariants = (value: unknown): NavSlotVariant[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const variants: NavSlotVariant[] = [];
  for (const item of value) {
    if (!isPlainObject(item) || !isPlainObject(item.bindings)) {
      continue;
    }

    const key = optionalString(item.key);
    const label = optionalString(item.label);
    if (!key || !label || variants.some((variant) => variant.key === key)) {
      continue;
    }

    const isDefault = item.isDefault === true && !variants.some((variant) => variant.isDefault);
    variants.push({ key, label, isDefault, bindings: parseBindings(item.bindings) });
  }

  return variants.length > 0 ? variants : undefined;
};

const parsePart = (value: unknown): NavSlotPart | undefined => {
  if (value === "media" || value === "composition") {
    return value;
  }

  if (typeof value === "string" && value.startsWith("sec:") && value.length > "sec:".length) {
    return value as NavSlotPart;
  }

  return undefined;
};

/** Parse a raw record-service row into a `NavPage`. Null when required fields are missing. */
export const parseNavPage = (raw: Record<string, unknown>): NavPageRecord | null => {
  const key = optionalString(raw.key) ?? optionalString(raw.id);
  const label = optionalString(raw.label);
  const template = isTemplate(raw.template) ? raw.template : null;

  if (!key || !label || !template) {
    return null;
  }

  return {
    key,
    label,
    template,
    targetSchema: optionalString(raw.targetSchema),
    targetVersion: optionalString(raw.targetVersion),
    isDefault: typeof raw.isDefault === "boolean" ? raw.isDefault : false,
    lifecycleControls: raw.lifecycleControls === "status-only" ? "status-only" : "interactive",
    permission: optionalString(raw.permission),
    enabled: typeof raw.enabled === "boolean" ? raw.enabled : true,
    description: optionalString(raw.description),
    variants: parseVariants(raw.variants),
  };
};

/** Parse a raw record-service row into a `NavPageSlot`. Null when required fields are missing. */
export const parseNavPageSlot = (raw: Record<string, unknown>): NavPageSlotRecord | null => {
  const key = optionalString(raw.key) ?? optionalString(raw.id);
  const page = optionalString(raw.page);
  const order = typeof raw.order === "number" ? raw.order : null;
  const type = isSlotType(raw.type) ? raw.type : null;

  if (!key || !page || order === null || !type) {
    return null;
  }

  // Missing region defaults to body (schema default). A present but unknown
  // region drops the row, same as an unknown type.
  let region: NavSlotRegion;
  if (raw.region === undefined || raw.region === null || raw.region === "") {
    region = "body";
  } else if (isRegion(raw.region)) {
    region = raw.region;
  } else {
    return null;
  }

  const childPage = optionalString(raw.childPage);
  const childPages = stringList(raw.childPages);
  if (childPages && !childPage && import.meta.env.DEV) {
    console.warn(`[page-model] ignoring childPages on ${key}: childPage is required`);
  }

  const view = optionalString(raw.view);

  return {
    key,
    page,
    order,
    type,
    region,
    label: optionalString(raw.label),
    description: optionalString(raw.description),
    slug: optionalString(raw.slug),
    group: optionalString(raw.group),
    targetSchema: optionalString(raw.targetSchema),
    targetVersion: optionalString(raw.targetVersion),
    view,
    format: isLandingFormat(raw.format) ? raw.format : undefined,
    detailPage: optionalString(raw.detailPage),
    detailChrome: type === "properties" && raw.detailChrome === "record" ? "record" : undefined,
    childPage,
    childPages: childPage ? childPages : undefined,
    variants: type === "compose" ? parseVariants(raw.variants) : undefined,
    component: optionalString(raw.component),
    bindSource: isBindSource(raw.bindSource) ? raw.bindSource : "none",
    bindSourceSlot: optionalString(raw.bindSourceSlot),
    bindRelationship: optionalString(raw.bindRelationship),
    bindPath: parseBindPath(raw.bindPath),
    bindEdgeOn: isBindEdgeOn(raw.bindEdgeOn) ? raw.bindEdgeOn : "target",
    bindField: optionalString(raw.bindField),
    bindParentKey: optionalString(raw.bindParentKey),
    bindFacetSlot: optionalString(raw.bindFacetSlot),
    emitsSelection: typeof raw.emitsSelection === "boolean" ? raw.emitsSelection : false,
    isRecordHome: type === "collection" && raw.isRecordHome === true ? true : undefined,
    showCount: typeof raw.showCount === "boolean" ? raw.showCount : false,
    allowViewSwitch: typeof raw.allowViewSwitch === "boolean" ? raw.allowViewSwitch : undefined,
    showHeading: typeof raw.showHeading === "boolean" ? raw.showHeading : undefined,
    part: type === "properties" && view ? parsePart(raw.part) : undefined,
    field: optionalString(raw.field),
    textValue: optionalString(raw.textValue),
    tone: isTone(raw.tone) ? raw.tone : "default",
    permission: optionalString(raw.permission),
    enabled: typeof raw.enabled === "boolean" ? raw.enabled : true,
  };
};
